import { MORE_FILTERS_BOOLEAN_OPTIONS } from 'client-portal/utils/clinicianSearch/more-filters-boolean-options';
import {
  SELF_PAY_INSURANCE_VALUE,
  isSelfPayInsuranceFilter,
} from 'client-portal/utils/clinicianSearch/insurance-facet-options';
import {
  parseBooleanQueryParam,
  parseCommaSeparatedInts,
  parseCommaSeparatedStrings,
  parseTrimmedString,
} from 'client-portal/utils/clinicianSearch/comma-query-param';
import { routeParamsWithKeysNulled } from 'client-portal/utils/clinicianSearch/facets/route-params';

const BLOCK_FILTER_KEYS = ['availableDays', 'availableTimes', 'officeIds', 'cptCodes'];

const FACET_EXCLUDE_KEYS = {
  officeIds: ['officeIds'],
  availableDays: ['availableDays'],
  availableTimes: ['availableTimes'],
  cptCodes: ['cptCodes'],
};

const BLOCK_FACET_SPECS = {
  availableTimes: { excludeKey: 'availableTimes', collect: b => b.timeBuckets ?? [] },
  availableDays: { excludeKey: 'availableDays', collect: b => b.weekdays ?? [] },
  officeIds: { excludeKey: 'officeIds', collect: b => b.officeIds ?? [] },
  cptCodes: { excludeKey: 'cptCodes', collect: b => b.cptCodeRateIds ?? [] },
};

function normalizedKeys(values) {
  let keys = new Set();
  for (let value of values) {
    let key = String(value ?? '').trim();
    if (key) {
      keys.add(key);
    }
  }
  return keys;
}

function tally(map, keys) {
  for (let key of keys) {
    map.set(key, (map.get(key) ?? 0) + 1);
  }
}

function mapToBuckets(map) {
  return [...map.entries()].map(([key, count]) => ({ key, count }));
}

function blockAllowed(block, allow) {
  return !allow || (block.officeIds ?? []).some(id => allow.has(String(id)));
}

function filtersNeedBlock(filters, excludeKeys) {
  return BLOCK_FILTER_KEYS.some(k => !excludeKeys.includes(k) && filters[k].length);
}

function officeIdCounts(record) {
  let counts = new Map();
  for (let block of record?.availabilityBlocks ?? []) {
    for (let id of block?.officeIds ?? []) {
      let key = String(id ?? '').trim();
      if (key) {
        tally(counts, [key]);
      }
    }
  }
  return counts;
}

function blockOffices(record, getOfficeById) {
  let offices = [];
  for (let block of record?.availabilityBlocks ?? []) {
    for (let id of block?.officeIds ?? []) {
      let office = getOfficeById(String(id));
      if (office) {
        offices.push(office);
      }
    }
  }
  return offices;
}

export function officesForCptCodeRateAndClinicianId(records, cptCodeRateId, clinicianId) {
  let clinician = records.find(record => String(record.id) === String(clinicianId));
  let blocks =
    clinician?.availabilityBlocks?.filter(block => block.cptCodeRateIds.includes(cptCodeRateId)) ??
    [];
  return blocks.reduce((acc, block) => {
    let officeIds = block?.officeIds.flat() ?? [];
    acc = [...officeIds, ...acc];
    return acc;
  }, []);
}

export function cptCodeRateIdsForClinicianId(records, clinicianId, { officeIds = [] } = {}) {
  let clinician = records.find(r => String(r.id) === String(clinicianId));
  if (!clinician) {
    return [];
  }

  let allow = officeIds.length ? new Set(officeIds.map(String)) : null;
  let rateIds = new Set();

  for (let block of clinician.availabilityBlocks ?? []) {
    if (!blockAllowed(block, allow)) {
      continue;
    }
    for (let key of normalizedKeys(block.cptCodeRateIds ?? [])) {
      rateIds.add(key);
    }
  }

  return [...rateIds];
}

export function dominantOfficeIdForRecord(record) {
  let counts = officeIdCounts(record);
  if (counts.size === 0) {
    return null;
  }
  let entries = [...counts.entries()].sort(([a], [b]) =>
    String(a).localeCompare(String(b), undefined, { numeric: true })
  );
  let best = entries[0];
  for (let entry of entries) {
    if (entry[1] > best[1]) {
      best = entry;
    }
  }
  return best[0];
}

export function officeIdsForRecord(record, { matchBlock } = {}) {
  let ids = new Set();
  for (let block of record?.availabilityBlocks ?? []) {
    if (matchBlock && !matchBlock(block)) {
      continue;
    }
    for (let id of block?.officeIds ?? []) {
      let key = String(id ?? '').trim();
      if (key) {
        ids.add(key);
      }
    }
  }
  return [...ids];
}

// Office ids the clinician has availability at *under the active filters*. A
// block must satisfy every applied filter (days/times/cptCodes/officeIds) to
// contribute its offices, and when an office filter is applied we further
// restrict the result to the selected offices so a matching block can't leak
// its other offices into the modality.
export function filteredOfficeIdsForRecord(record, routeParams = {}) {
  let filters = filterParamsFromRouteParams(routeParams);
  let ids = officeIdsForRecord(record, {
    matchBlock: block => blockMatchesFilters(block, filters),
  });

  if (!filters.officeIds.length) {
    return ids;
  }

  let allow = new Set(filters.officeIds.map(String));
  return ids.filter(id => allow.has(id));
}

export function modalityFlagsForRecord(record, getOfficeById) {
  let offices = blockOffices(record, getOfficeById);
  return {
    hasInPerson: offices.some(o => !o.isVideo),
    hasTelehealth: offices.some(o => o.isVideo),
  };
}

function filterParamsFromRouteParams(routeParams = {}) {
  return {
    name: parseTrimmedString(routeParams.name),
    specialty: parseTrimmedString(routeParams.specialty),
    insurance: parseTrimmedString(routeParams.insurance),
    service: parseTrimmedString(routeParams.service),
    ageGroup: parseTrimmedString(routeParams.ageGroup),
    officeIds: parseCommaSeparatedStrings(routeParams.officeIds),
    availableDays: parseCommaSeparatedInts(routeParams.availableDays),
    availableTimes: parseCommaSeparatedInts(routeParams.availableTimes),
    cptCodes: parseCommaSeparatedStrings(routeParams.cptCodes),
    globalMonarchChannelId: parseTrimmedString(routeParams.globalMonarchChannelId),
    treatmentType: parseTrimmedString(routeParams.treatmentType),
    ...Object.fromEntries(
      MORE_FILTERS_BOOLEAN_OPTIONS.map(spec => [
        spec.param,
        parseBooleanQueryParam(routeParams[spec.param]),
      ])
    ),
  };
}

function clinicianMatchesName(record, nameFilter) {
  if (!nameFilter) {
    return true;
  }
  let needle = nameFilter.toLowerCase();
  let hay = `${record.firstName ?? ''} ${record.lastName ?? ''}`.toLowerCase();
  return hay.includes(needle);
}

function listFieldMatchesFilter(values, filter) {
  let needle = filter.toLowerCase();
  return (values ?? []).some(value => parseTrimmedString(value).toLowerCase() === needle);
}

function clinicianMatchesSpecialty(record, specialtyFilter) {
  if (!specialtyFilter) {
    return true;
  }

  return listFieldMatchesFilter(record.marketingSpecialties, specialtyFilter);
}

function clinicianMatchesInsurance(record, insuranceFilter) {
  if (!insuranceFilter) {
    return true;
  }

  if (isSelfPayInsuranceFilter(insuranceFilter)) {
    return !!record.acceptSelfPayCashIndicator;
  }

  return listFieldMatchesFilter(record.marketingInsuranceProviders, insuranceFilter);
}

function clinicianMatchesTreatmentType(record, treatmentTypeFilter) {
  if (!treatmentTypeFilter) {
    return true;
  }

  return listFieldMatchesFilter(record.treatmentTypes, treatmentTypeFilter);
}

function clinicianMatchesApproach(record, serviceFilter) {
  if (!serviceFilter) {
    return true;
  }

  return listFieldMatchesFilter(record.marketingServices, serviceFilter);
}

function clinicianMatchesAgeGroup(record, ageGroupFilter) {
  if (!ageGroupFilter) {
    return true;
  }

  return listFieldMatchesFilter(record.ageGroups, ageGroupFilter);
}

// Attribute filters are ANDed; availability filters are applied separately against blocks.
const ATTRIBUTE_MATCHERS = [
  { filterKey: 'name', matches: clinicianMatchesName },
  { filterKey: 'specialty', matches: clinicianMatchesSpecialty },
  { filterKey: 'insurance', matches: clinicianMatchesInsurance },
  { filterKey: 'treatmentType', matches: clinicianMatchesTreatmentType },
  { filterKey: 'service', matches: clinicianMatchesApproach },
  { filterKey: 'ageGroup', matches: clinicianMatchesAgeGroup },
];

const BOOLEAN_ATTRIBUTE_MATCHERS = MORE_FILTERS_BOOLEAN_OPTIONS.map(spec => ({
  filterKey: spec.param,
  indicatorKey: spec.indicator,
  matches: clinicianMatchesBooleanIndicator,
}));

function clinicianMatchesBooleanIndicator(record, isActive, indicatorKey) {
  if (!isActive) {
    return true;
  }

  return !!record?.[indicatorKey];
}

function blockMatchesFilters(block, filters, excludeKeys = []) {
  let skip = new Set(excludeKeys);

  let weekdayOk =
    skip.has('availableDays') ||
    !filters.availableDays.length ||
    block.weekdays?.some(d => filters.availableDays.includes(d));

  let bucketOk =
    skip.has('availableTimes') ||
    !filters.availableTimes.length ||
    block.timeBuckets?.some(b => filters.availableTimes.includes(b));

  let officeOk =
    skip.has('officeIds') ||
    !filters.officeIds.length ||
    block.officeIds?.some(id => filters.officeIds.includes(String(id)));

  let cptOk =
    skip.has('cptCodes') ||
    !filters.cptCodes.length ||
    block.cptCodeRateIds?.some(id => filters.cptCodes.includes(String(id)));

  return weekdayOk && bucketOk && officeOk && cptOk;
}

function recordMatchesAvailabilityFilters(record, filters, excludeKeys = []) {
  let needsBlock = filtersNeedBlock(filters, excludeKeys);

  if (!record.availabilityBlocks?.length) {
    return !needsBlock;
  }

  return record.availabilityBlocks.some(block => blockMatchesFilters(block, filters, excludeKeys));
}

export function clinicianMatchesFilters(record, routeParams = {}) {
  let filters = filterParamsFromRouteParams(routeParams);

  return (
    ATTRIBUTE_MATCHERS.every(({ filterKey, matches }) => matches(record, filters[filterKey])) &&
    BOOLEAN_ATTRIBUTE_MATCHERS.every(({ filterKey, indicatorKey, matches }) =>
      matches(record, filters[filterKey], indicatorKey)
    ) &&
    recordMatchesAvailabilityFilters(record, filters)
  );
}

export function filterClinicians(records, routeParams = {}) {
  return records.filter(record => clinicianMatchesFilters(record, routeParams));
}

function keysForRecord(record, filters, excludeKeys, collect) {
  let keys = new Set();
  for (let block of record.availabilityBlocks ?? []) {
    if (!blockMatchesFilters(block, filters, excludeKeys)) {
      continue;
    }
    for (let key of normalizedKeys(collect(block))) {
      keys.add(key);
    }
  }
  return keys;
}

function facetBucketsFromBlocks(records, filters, facet) {
  let excludeKeys = FACET_EXCLUDE_KEYS[facet.excludeKey] ?? [];
  let byKey = new Map();

  for (let record of records) {
    if (
      !ATTRIBUTE_MATCHERS.every(({ filterKey, matches }) => matches(record, filters[filterKey]))
    ) {
      continue;
    }
    if (!recordMatchesAvailabilityFilters(record, filters, excludeKeys)) {
      continue;
    }
    tally(byKey, keysForRecord(record, filters, excludeKeys, facet.collect));
  }

  return mapToBuckets(byKey);
}

function nameFacetBucketsFromRecords(records) {
  let byKey = new Map();
  for (let record of records) {
    let fn = String(record.firstName ?? '')
      .trim()
      .toLocaleLowerCase();
    if (fn) {
      tally(byKey, [fn]);
    }
  }
  return mapToBuckets(byKey);
}

function listFieldFacetBucketsFromRecords(records, field) {
  let byKey = new Map();
  for (let record of records) {
    tally(byKey, normalizedKeys(record[field] ?? []));
  }
  return mapToBuckets(byKey);
}

function marketingInsuranceProviderKeys(record) {
  return [...normalizedKeys(record.marketingInsuranceProviders ?? [])].filter(
    key => !isSelfPayInsuranceFilter(key)
  );
}

function insuranceFacetBucketsFromRecords(records) {
  let byKey = new Map();
  for (let record of records) {
    tally(byKey, marketingInsuranceProviderKeys(record));
    if (record.acceptSelfPayCashIndicator) {
      tally(byKey, [SELF_PAY_INSURANCE_VALUE]);
    }
  }
  return mapToBuckets(byKey);
}

function treatmentTypeFacetBucketsFromRecords(records) {
  let byKey = new Map();
  for (let record of records) {
    tally(byKey, normalizedKeys(record.treatmentTypes ?? []));
  }
  return mapToBuckets(byKey);
}

export function buildFacetsMetaForRecords(
  records,
  { pageNumber = 1, pageSize = 10, routeParams = {} } = {}
) {
  let filters = filterParamsFromRouteParams(routeParams);

  return {
    page: { number: pageNumber, pageSize, total: records.length },
    facets: {
      availableTimes: facetBucketsFromBlocks(records, filters, BLOCK_FACET_SPECS.availableTimes),
      availableDays: facetBucketsFromBlocks(records, filters, BLOCK_FACET_SPECS.availableDays),
      offices: facetBucketsFromBlocks(records, filters, BLOCK_FACET_SPECS.officeIds),
      cptCodeRates: facetBucketsFromBlocks(records, filters, BLOCK_FACET_SPECS.cptCodes),
      names: nameFacetBucketsFromRecords(records),
      specialties: listFieldFacetBucketsFromRecords(records, 'marketingSpecialties'),
      insurances: insuranceFacetBucketsFromRecords(records),
      treatmentTypes: treatmentTypeFacetBucketsFromRecords(records),
      approaches: listFieldFacetBucketsFromRecords(records, 'marketingServices'),
      ageGroups: listFieldFacetBucketsFromRecords(records, 'ageGroups'),
    },
  };
}

export function routeParamsForFacetsExcluding(routeParams, excludeParamKeys = []) {
  return {
    ...routeParamsWithKeysNulled(routeParams, excludeParamKeys),
    pageNumber: 1,
  };
}

export function buildFacetsMetaForRouteParams(records, routeParams, excludeParamKeys = []) {
  let cohortRouteParams = routeParamsForFacetsExcluding(routeParams, excludeParamKeys);
  let cohort = filterClinicians(records, cohortRouteParams);
  return buildFacetsMetaForRecords(cohort, { routeParams: cohortRouteParams });
}

export function probeNameMatch(records, routeParams, nameKey) {
  let name = String(nameKey ?? '').trim();
  let probeParams = { ...routeParams, name: name || null };
  return filterClinicians(records, probeParams).length > 0 ? 'yes' : 'no';
}

const ENRICHMENT_BUCKET_ORDER = {
  hasSlot: 0,
  laterSlot: 1,
  unknown: 2,
  error: 3,
  noSlot: 4,
};

function compareLastName(recordA, recordB) {
  let lastA = String(recordA?.lastName ?? '').toLowerCase();
  let lastB = String(recordB?.lastName ?? '').toLowerCase();
  if (lastA !== lastB) {
    return lastA.localeCompare(lastB);
  }
  return String(recordA?.firstName ?? '')
    .toLowerCase()
    .localeCompare(String(recordB?.firstName ?? '').toLowerCase());
}

function compareIsoAsc(isoA, isoB) {
  let timeA = isoA ? Date.parse(isoA) : 0;
  let timeB = isoB ? Date.parse(isoB) : 0;
  if (Number.isNaN(timeA)) {
    timeA = 0;
  }
  if (Number.isNaN(timeB)) {
    timeB = 0;
  }
  return timeA - timeB;
}

export function sortCohortByEnrichment(records, getStatus, getSlot) {
  if (!records?.length) {
    return [];
  }

  let indexed = records.map((record, index) => ({ record, index }));

  indexed.sort((a, b) => {
    let statusA = getStatus(String(a.record.id));
    let statusB = getStatus(String(b.record.id));
    let bucketA = ENRICHMENT_BUCKET_ORDER[statusA] ?? ENRICHMENT_BUCKET_ORDER.unknown;
    let bucketB = ENRICHMENT_BUCKET_ORDER[statusB] ?? ENRICHMENT_BUCKET_ORDER.unknown;

    if (bucketA !== bucketB) {
      return bucketA - bucketB;
    }

    if (statusA === 'hasSlot' && statusB === 'hasSlot') {
      let slotCmp = compareIsoAsc(getSlot(String(a.record.id)), getSlot(String(b.record.id)));
      if (slotCmp !== 0) {
        return slotCmp;
      }
      return compareLastName(a.record, b.record);
    }

    if (statusA === 'unknown' && statusB === 'unknown') {
      return a.index - b.index;
    }

    if (
      (statusA === 'laterSlot' || statusA === 'noSlot' || statusA === 'error') &&
      (statusB === 'laterSlot' || statusB === 'noSlot' || statusB === 'error')
    ) {
      return compareLastName(a.record, b.record);
    }

    return 0;
  });

  return indexed.map(entry => entry.record);
}

export function paginateRecords(records, pageNumber, pageSize) {
  let num = parseInt(String(pageNumber ?? 1), 10);
  if (Number.isNaN(num) || num < 1) {
    num = 1;
  }
  let size = parseInt(String(pageSize ?? 10), 10) || 10;
  let start = (num - 1) * size;
  return {
    pageNumber: num,
    pageSize: size,
    pageRecords: records.slice(start, start + size),
    total: records.length,
  };
}

function clinicianSearchUrl(adapter, { nextSlots } = {}) {
  let ns = String(adapter.namespace ?? '').replace(/^\/+|\/+$/g, '');
  let path = `${ns}/clinician-search${nextSlots ? '/next-slots' : ''}`;

  if (adapter.host) {
    return `${String(adapter.host).replace(/\/+$/, '')}/${path}`;
  }

  return `/${path}`;
}

export function adapterHelper(adapter, params) {
  let { clinicianSearch, nextSlots } = params ?? {};
  if (!clinicianSearch && !nextSlots) {
    return undefined;
  }

  delete params.clinicianSearch;
  delete params.nextSlots;

  return clinicianSearchUrl(adapter, { nextSlots });
}
